import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { seedDatabase, clearSeededData, sampleUsers, sampleJobs, sampleProjects, sampleGroupPosts, sampleGroupComments } from '../../utils/seedData';

const DataSeeder = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [message, setMessage] = useState('');

  const isAdmin = hasRole('admin');

  const handleSeedData = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setAction('seed');
    setMessage('Creating sample data...');

    try {
      const result = await seedDatabase();
      setMessage(`✅ Sample data created successfully! ${result.users} users, ${result.jobs} jobs, ${result.projects} projects, ${result.posts} posts, ${result.comments} comments.`);
    } catch (error) {
      console.error('Error seeding data:', error);
      setMessage(`❌ Error creating sample data: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
      setAction('');
    }
  };

  const handleClearSeededData = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to remove ALL seeded/sample data? This cannot be undone.')) return;

    setLoading(true);
    setAction('clear');
    setMessage('Removing seeded data...');

    try {
      const deleted = await clearSeededData();
      setMessage(`✅ Seeded data removed: ${deleted.users} users, ${deleted.groups} groups, ${deleted.posts} posts, ${deleted.comments} comments, ${deleted.jobs} jobs, ${deleted.projects} projects.`);
    } catch (error) {
      console.error('Error clearing seeded data:', error);
      setMessage(`❌ Error removing seeded data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      setAction('');
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600 mt-2">You don't have permission to access this tool.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Database Seeder</h1>
        <p className="text-gray-500 mb-6">Create or remove sample data to demonstrate site capabilities. All seeded data is tracked with an <code className="bg-gray-100 px-1 rounded text-sm">isSeeded</code> flag and clearly separated from real users in the admin dashboard.</p>

        {/* What gets created */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">What Gets Created</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <span className="text-lg mr-2">👤</span>
                <span className="font-medium text-blue-900">{sampleUsers.length} Sample Users</span>
              </div>
              <ul className="text-xs text-blue-700 space-y-0.5 ml-7">
                {sampleUsers.map(u => (
                  <li key={u.id}>{u.displayName} — {u.jobTitle}, {u.company}</li>
                ))}
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <span className="text-lg mr-2">💼</span>
                <span className="font-medium text-green-900">{sampleJobs.length} Job Postings</span>
              </div>
              <ul className="text-xs text-green-700 space-y-0.5 ml-7">
                {sampleJobs.map((j, i) => (
                  <li key={i}>{j.title} — {j.company}</li>
                ))}
              </ul>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <span className="text-lg mr-2">📋</span>
                <span className="font-medium text-purple-900">{sampleProjects.length} Project Postings</span>
              </div>
              <ul className="text-xs text-purple-700 space-y-0.5 ml-7">
                {sampleProjects.map((p, i) => (
                  <li key={i}>{p.title}</li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center mb-1">
                <span className="text-lg mr-2">💬</span>
                <span className="font-medium text-orange-900">{sampleGroupPosts.length} Group Posts + {sampleGroupComments.length} Comments</span>
              </div>
              <p className="text-xs text-orange-700 ml-7">Discussions with likes, replies, and user interactions across groups</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handleSeedData}
            disabled={loading}
            className="bg-[#3d67ad] text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && action === 'seed' ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : '🌱 Seed Sample Data'}
          </button>

          <button
            onClick={handleClearSeededData}
            disabled={loading}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && action === 'clear' ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Removing...
              </>
            ) : '🗑️ Remove All Seeded Data'}
          </button>
        </div>

        {message && (
          <div className={`mt-6 p-4 rounded-md ${
            message.includes('✅') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        {message.includes('created successfully') && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Next Steps:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Visit <a href="/directory" className="underline font-medium">/directory</a> to see sample member profiles</li>
              <li>• Visit <a href="/groups" className="underline font-medium">/groups</a> to see groups with discussions</li>
              <li>• Visit <a href="/jobs" className="underline font-medium">/jobs</a> to see job postings with applications</li>
              <li>• Visit <a href="/projects" className="underline font-medium">/projects</a> to see project postings</li>
              <li>• Visit <a href="/admin" className="underline font-medium">/admin</a> → All Users tab to see seeded vs real tracking</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataSeeder;
